// Battery health page (Phase 2 Slice A).
//
// Sortable table of battery-powered entities with predicted-days-remaining,
// 30d trend, and a "needs attention" banner. Empty state explains the 7-day
// warm-up window so first-week users don't see a broken feature.

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext.js';
import { health, type BatteriesResponse, type BatteryEntry } from '../../lib/api.js';

type SortKey = 'percent' | 'predicted' | 'name';

function formatDaysRemaining(days: number | null, lowConfidence: boolean): string {
  if (days === null) return '—';
  const suffix = lowConfidence ? ' (low confidence)' : '';
  if (days === 0) return `today${suffix}`;
  if (days < 30) return `${days} days${suffix}`;
  if (days < 365) return `${Math.round(days / 30)} months${suffix}`;
  return `${Math.round(days / 365)} years${suffix}`;
}

function trendLabel(trend: number | null): string {
  if (trend === null) return '—';
  if (trend > -0.5) return 'flat';
  return `${trend.toFixed(1)} %/30d`;
}

export default function BatteriesPage() {
  const { currentHomeId } = useAuth();
  const [data, setData] = useState<BatteriesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('percent');

  useEffect(() => {
    if (!currentHomeId) return;
    let cancelled = false;
    setLoading(true);
    health
      .batteries(currentHomeId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Could not load battery data.';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentHomeId]);

  const sorted = useMemo<BatteryEntry[]>(() => {
    if (!data) return [];
    const copy = [...data.batteries];
    copy.sort((a, b) => {
      if (sortKey === 'percent') return a.percent - b.percent;
      if (sortKey === 'name') return a.name.localeCompare(b.name);
      const av = a.predictedDaysRemaining ?? Number.POSITIVE_INFINITY;
      const bv = b.predictedDaysRemaining ?? Number.POSITIVE_INFINITY;
      return av - bv;
    });
    return copy;
  }, [data, sortKey]);

  if (!currentHomeId) {
    return <p style={{ color: 'var(--ac-text-muted)' }}>Select a home first.</p>;
  }

  if (loading) {
    return <p style={{ color: 'var(--ac-text-muted)' }}>Loading battery data…</p>;
  }

  if (error) {
    return (
      <div role="alert" style={{ color: 'var(--ac-danger, crimson)' }}>
        {error}
      </div>
    );
  }

  if (!data || data.batteries.length === 0) {
    return (
      <section aria-labelledby="batteries-empty">
        <h1 id="batteries-empty" className="text-xl font-semibold mb-2">
          Batteries
        </h1>
        <p style={{ color: 'var(--ac-text-muted)' }}>
          No battery-powered devices found in this home yet. As your bridge syncs state, devices
          with a battery percentage will appear here. Predictions take about 7 days of samples to
          become useful.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="batteries-heading">
      <header className="mb-4 flex items-baseline justify-between">
        <h1 id="batteries-heading" className="text-xl font-semibold">
          Batteries
        </h1>
        <p style={{ color: 'var(--ac-text-muted)' }}>
          {data.batteries.length} device{data.batteries.length === 1 ? '' : 's'}
        </p>
      </header>

      {data.attentionCount > 0 && (
        <div
          role="status"
          className="mb-4 p-3 rounded"
          style={{
            background: 'var(--ac-warn-bg, rgba(255, 200, 0, 0.12))',
            color: 'var(--ac-warn-fg, #8a5a00)',
          }}
        >
          {data.attentionCount} device{data.attentionCount === 1 ? '' : 's'} need attention (≤20% or
          predicted to die within two weeks).
        </div>
      )}

      <div className="mb-3 flex gap-2 text-sm" role="group" aria-label="Sort">
        <span style={{ color: 'var(--ac-text-muted)' }}>Sort by:</span>
        {(['percent', 'predicted', 'name'] as SortKey[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setSortKey(k)}
            aria-pressed={sortKey === k}
            style={{
              fontWeight: sortKey === k ? 600 : 400,
              textDecoration: sortKey === k ? 'underline' : 'none',
            }}
          >
            {k === 'percent' ? 'Lowest %' : k === 'predicted' ? 'Soonest dead' : 'Name'}
          </button>
        ))}
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--ac-border)' }}>
            <th scope="col" className="py-2">
              Device
            </th>
            <th scope="col">%</th>
            <th scope="col">Trend</th>
            <th scope="col">Predicted remaining</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((b) => (
            <tr
              key={b.entityId}
              style={{
                borderBottom: '1px solid var(--ac-border-faint)',
                background: b.needsAttention
                  ? 'var(--ac-warn-bg, rgba(255, 200, 0, 0.06))'
                  : 'transparent',
              }}
            >
              <th scope="row" className="py-2" style={{ fontWeight: 500 }}>
                {b.name}
                {b.room && (
                  <span style={{ color: 'var(--ac-text-muted)', marginLeft: 6 }}>· {b.room}</span>
                )}
              </th>
              <td>{b.percent}%</td>
              <td>{trendLabel(b.trendPercentPer30d)}</td>
              <td>{formatDaysRemaining(b.predictedDaysRemaining, b.lowConfidence)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
